import styled from '@emotion/styled'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import Breakpoint from '../../../Breakpoint'
import Loader from '../../../components/global/Loader'
import SearchInput from '../../../components/global/SearchInput'
import SkillCard from '../../../components/global/SkillCard'
import Title from '../../../components/global/Title'
import UserContext from '../../../context/UserContext'
import { apiClient } from '../../../services/apiClient'
import { ParentSkill, Skill } from '../../../types/skill.type'
import { StyledButton } from '../../admin/Login'
import { SkillsWrapper } from '../my-members/CoachMyMembers'
import CoachParentSkillFormModal from './CoachParentSkillFormModal'

const TitleWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 20px;

    & > div {
        flex: 1;
        width: 100%;
    }

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        flex-direction: column;
        gap: 0;
    }
`

export default function CoachParentSkills() {
    const user = useContext(UserContext)
    const { t } = useTranslation()

    const [parentSkillToEdit, setParentSkillToEdit] = useState<ParentSkill>()
    const [showEditSkillModal, setShowEditSkillModal] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')

    const {
        data: parentSkills = [],
        isLoading,
        refetch: refetchParentSkills,
    } = useQuery<Skill[]>({
        queryKey: ['parentSkills'],
        queryFn: async () => {
            const response = await apiClient.get('/parent-skills', {
                params: {
                    role: 'ROLE_COACH',
                },
            })
            return response.data
        },
    })

    const { mutate: deleteParentSkill } = useMutation({
        mutationFn: async (id: number) => {
            try {
                await apiClient.delete(`/parent-skills/${id}`, {
                    params: {
                        role: 'ROLE_COACH',
                    },
                })
                toast.success(t('coach.skill-deleted'))
                refetchParentSkills()
            } catch (error) {
                console.error('Error deleting parent skill:', error)
                toast.error(t('coach.skill-delete-error'))
            }
        },
    })

    const filteredParentSkills = useMemo(() => {
        if (!parentSkills) return []
        return parentSkills.filter((skill: Skill) => skill.name.toLowerCase().includes(searchTerm.toLowerCase()))
    }, [parentSkills, searchTerm])

    if (isLoading) return <Loader />

    const editParentSkill = (parentSkill: Skill) => {
        setParentSkillToEdit(parentSkill)
        setShowEditSkillModal(true)
    }

    return (
        <div>
            <TitleWrapper>
                <div>
                    <Title>{t('coach.parentSkills')}</Title>
                    <SearchInput
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Rechercher une catégorie de compétence..."
                    />
                </div>
                <StyledButton
                    className="btn btn-primary"
                    onClick={() => {
                        setParentSkillToEdit(undefined)
                        setShowEditSkillModal(true)
                    }}
                >
                    Créer une catégorie
                </StyledButton>
            </TitleWrapper>
            <SkillsWrapper>
                {filteredParentSkills.length === 0 ? (
                    <p>Aucune catégorie trouvée</p>
                ) : (
                    filteredParentSkills.map((parentSkill) => (
                        <SkillCard
                            key={parentSkill.id}
                            name={parentSkill.name}
                            deleteFn={() => deleteParentSkill(parentSkill.id)}
                            editFn={() => editParentSkill(parentSkill)}
                        />
                    ))
                )}
            </SkillsWrapper>
            {showEditSkillModal && (
                <CoachParentSkillFormModal
                    refetchParentSkills={refetchParentSkills}
                    setShowEditSkillModal={setShowEditSkillModal}
                    parentSkillToEdit={parentSkillToEdit}
                    setParentSkillToEdit={setParentSkillToEdit}
                />
            )}
        </div>
    )
}
